import type { FormElementWithCharsCounter } from "@/internals/mixins/with-chars-counter-mixin";

import FieldValidator, { type FieldValidatorStatus } from "./field-validator";

type ErrorType = "min" | "simpleMax" | "detailedMax" | "length" | "none";

const MESSAGES_MAP: Record<ErrorType, string> = {
    min: "Mínimo de {{minlength}} {{label}}. Você digitou {{currentLength}}.",
    simpleMax: "Limite de caracteres excedido",
    detailedMax: "Máximo de {{maxlength}} {{label}}. Você digitou {{currentLength}}.",
    length: "O valor inserido deve ter {{length}} {{label}}",
    none: ""
};

export default class LengthValidator implements FieldValidator {
    name: string = "length";

    status: FieldValidatorStatus = "error";

    errorType: ErrorType = "none";

    reportOnChange: boolean = false;

    minlength: string;

    maxlength: string;

    length: string;

    currentLength: string;

    getInvalidMessage(): string {
        return MESSAGES_MAP[this.errorType]
            .replace("{{maxlength}}", this.maxlength)
            .replace("{{minlength}}", this.minlength)
            .replace("{{length}}", this.length)
            .replace("{{label}}", this.getCharacterLabel())
            .replace("{{currentLength}}", this.currentLength);
    }

    validate(element: FormElementWithCharsCounter): boolean {
        this.resetState();

        const minlength = parseInt(element.getAttribute("minlength"), 10) ?? null;
        const maxlength = parseInt(element.getAttribute("maxlength"), 10) ?? null;
        const length = parseInt(element.getAttribute("length"), 10) ?? null;
        const elementValueLength = this.getElementValueLength(element);

        this.currentLength = elementValueLength.toString();

        if (length && elementValueLength > 0 && elementValueLength < length) {
            this.errorType = "length";
            this.length = length.toString();
        }

        if (minlength && elementValueLength > 0 && elementValueLength < minlength) {
            this.errorType = "min";
            this.minlength = minlength.toString();
        }

        if (maxlength && elementValueLength > maxlength) {
            const hasCharsCounter = element.hasCharsCounter();

            this.errorType = hasCharsCounter ? "simpleMax" : "detailedMax";
            this.reportOnChange = hasCharsCounter;
            this.maxlength = maxlength.toString();
        }

        return this.errorType === "none";
    }

    private getElementValueLength(element: FormElementWithCharsCounter) {
        return element.getCurrentCharsCounterValue();
    }

    private resetState() {
        this.errorType = "none";
        this.minlength = "";
        this.maxlength = "";
        this.length = "";
        this.reportOnChange = false;
        this.currentLength = "";
    }

    private getCharacterLabel() {
        if (this.errorType === "none") return "";

        const mapping: Record<Exclude<ErrorType, "none">, string> = {
            min: this.minlength,
            simpleMax: this.maxlength,
            detailedMax: this.maxlength,
            length: this.length
        };

        const valueToCompare = mapping[this.errorType];

        return valueToCompare === "1" ? "caractere" : "caracteres";
    }
}
